/**
 * Single commercial margin helper — always compare on NET basis when rates known.
 */

import type { FiscalBasis } from "./commercial-line.js";
import { taxLineFromAmount } from "./commercial-line.js";
import { marginOnNet, roundMoney } from "./index.js";

export type MarginResult = {
  saleNet: number;
  costNet: number;
  marginAmount: number;
  marginPct: number | null;
  comparable: boolean;
  note?: string;
};

/**
 * Sale amount + cost amount with optional fiscal bases.
 * If either rate is unknown for taxable, comparable=false and note explains.
 */
export function commercialMargin(input: {
  saleAmount: number;
  saleQty?: number;
  saleFiscal?: FiscalBasis | null;
  costAmount: number;
  costQty?: number;
  costFiscal?: FiscalBasis | null;
  /** Default cost basis when unknown: treat purchase cost as FINAL (historical FM). */
  defaultCostMode?: "net" | "final";
}): MarginResult {
  const saleQty = input.saleQty ?? 1;
  const costQty = input.costQty ?? 1;
  const sale = taxLineFromAmount(input.saleAmount, saleQty, input.saleFiscal);
  const costBasis: FiscalBasis = {
    priceMode: input.costFiscal?.priceMode ?? input.defaultCostMode ?? "final",
    taxCategory: input.costFiscal?.taxCategory ?? "taxable",
    taxRate: input.costFiscal?.taxRate ?? null,
  };
  const cost = taxLineFromAmount(input.costAmount, costQty, costBasis);
  const comparable = sale.rateKnown && cost.rateKnown;
  const saleNet = sale.netAmount;
  const costNet = cost.netAmount;
  const marginAmount = roundMoney(saleNet - costNet);
  const marginPct = marginOnNet(saleNet, costNet);
  return {
    saleNet,
    costNet,
    marginAmount,
    marginPct: marginPct == null ? null : roundMoney(marginPct * 100),
    comparable,
    note: comparable
      ? undefined
      : "No se puede calcular un margen comparable: falta alícuota en venta o en costo (no se asume 21%).",
  };
}
